import { Fragment } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import {
  CareerDot,
  CareerListBadge,
  CareerPageCompanyName,
  CareerPageDetails,
  CareerPageJobCard,
  CareerPageJobTitle,
  CareerPageLogo,
  CareerPageMetaInfo,
  CareerPagePlaceholderLogo,
} from '../styled'
import GeoAlt from '../../assets/svgs/GeoAlt'
import ClockOutline from '../../assets/svgs/ClockOutline'
import theme from '../../constants/theme'
import { convertToNormalText } from '../../utilities/stringUtilities'

const getBadgeStyle = (employmentType) => {
  switch (employmentType) {
    case 'full-time':
      return { bg: '#E6F7EF', color: theme.talentinoGreen }
    case 'part-time':
      return { bg: '#E5F3FF', color: theme.talentinoBlue }
    case 'contract':
      return { bg: '#FFF4E5', color: theme.talentinoDarkerOrange }
    case 'internship':
      return { bg: '#F3E8FF', color: '#7C3AED' }
    default:
      return {
        bg: theme.talentinoLightBlueBackground,
        color: theme.talentinoBlue,
      }
  }
}

export function JobListItem({
  company,
  title,
  startDate,
  city,
  country,
  employmentType,
  logoUrl,
  isHighlighted,
  viewPosition,
  getInitials,
}) {
  function timeSince(startDate) {
    const startTime = new Date(startDate).getTime()
    const currentTime = Date.now()
    const elapsedTime = currentTime - startTime

    const units = [
      { label: 'year', seconds: 365 * 24 * 60 * 60 },
      { label: 'month', seconds: 30 * 24 * 60 * 60 },
      { label: 'week', seconds: 7 * 24 * 60 * 60 },
      { label: 'day', seconds: 24 * 60 * 60 },
      { label: 'hour', seconds: 60 * 60 },
      { label: 'minute', seconds: 60 },
      { label: 'second', seconds: 1 },
    ]

    const { label, seconds } = _.find(
      units,
      ({ seconds }) => elapsedTime / 1000 >= seconds,
    )

    const count = Math.floor(elapsedTime / 1000 / seconds)

    return count === 1 ? `1 ${label} ago` : `${count} ${label}s ago`
  }

  const badge = getBadgeStyle(employmentType)

  const locationItems = [
    { key: 'city', value: city },
    { key: 'country', value: country },
  ]
    .filter(({ value }) => Boolean(value))
    .map((item, index) => ({
      ...item,
      icon:
        index === 0 ? <GeoAlt width="12" height="12" color="#9CA3AF" /> : null,
    }))

  const metaItems = [
    ...locationItems,
    ...(startDate
      ? [
          {
            key: 'startDate',
            value: <time>{timeSince(startDate)}</time>,
            icon: <ClockOutline width={12} height={12} color="#9CA3AF" />,
          },
        ]
      : []),
  ]

  return (
    <CareerPageJobCard isHighlighted={isHighlighted} onClick={viewPosition}>
      {logoUrl ? (
        <CareerPageLogo
          loading="lazy"
          src={logoUrl}
          alt={`${company?.companyName} logo`}
          hasWideAspect={logoUrl?.includes('ext_2-')}
        />
      ) : (
        <CareerPagePlaceholderLogo>
          {getInitials(company?.companyName)}
        </CareerPagePlaceholderLogo>
      )}
      <CareerPageDetails>
        <CareerPageCompanyName>{company?.companyName}</CareerPageCompanyName>
        <CareerPageJobTitle>{title}</CareerPageJobTitle>
        {metaItems.length > 0 && (
          <CareerPageMetaInfo>
            {metaItems.map(({ key, value, icon }, index) => (
              <Fragment key={key}>
                {index > 0 && <CareerDot />}
                {icon}
                {typeof value === 'string' ? <span>{value}</span> : value}
              </Fragment>
            ))}
          </CareerPageMetaInfo>
        )}
      </CareerPageDetails>
      {employmentType && (
        <CareerListBadge bg={badge.bg} color={badge.color}>
          {convertToNormalText(employmentType)}
        </CareerListBadge>
      )}
    </CareerPageJobCard>
  )
}

JobListItem.propTypes = {
  company: PropTypes.shape({
    companyName: PropTypes.string.isRequired,
  }).isRequired,
  title: PropTypes.string.isRequired,
  startDate: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  employmentType: PropTypes.string,
  logoUrl: PropTypes.string,
  isHighlighted: PropTypes.bool.isRequired,
  viewPosition: PropTypes.func.isRequired,
  getInitials: PropTypes.func.isRequired,
}
